var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo } from 'react';
import { Button, Icon, Typography } from '@pinwheel/origami';
import { useOpts } from '../context';
import { EventAction } from '../types/events';
import useLinkNavigate from '../hooks/useLinkNavigate';
import LogoHeader from '../LinkApiRouter/components/LogoHeader';
import useShowingExitConfirmation from '../hooks/useShowingExitConfirmation';
import { ScreenTitleHeader } from './ScreenTitleHeader';
import { useGoBackEventListener } from '../hooks/useGoBackEventListener';
import { selectLinkTokenPayload } from '../state/slices/slice-link-params';
import { checkHackyWorkspace } from '../utils/hacky-workspace-customizations';
import { useAppSelector } from '../state';
import { useGetLocalizedString } from '../hooks/useGetLocalizedString';
import useLinkSolution from '../hooks/useLinkSolution';
var HeaderButton = function (_a) {
    var children = _a.children, margin = _a.margin, thin = _a.thin, rest = __rest(_a, ["children", "margin", "thin"]);
    return (React.createElement(Button, __assign({ variant: "ghost", color: "neutral", type: "button" }, rest, { sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: thin ? '36px' : '48px',
            minHeight: thin ? '36px' : '48px',
            minWidth: thin ? '36px' : '48px',
            margin: "".concat(margin || 0, " !important"),
        } }), children));
};
var ModalHeader = function (_a) {
    var hideExitButton = _a.hideExitButton, hideBackButton = _a.hideBackButton, showPlatformLogo = _a.showPlatformLogo, onInfo = _a.onInfo, testid = _a["data-testid"], titleTestId = _a["title-testid"], title = _a.title, onBack = _a.onBack, disableExitConfirmation = _a.disableExitConfirmation, clampTitleText = _a.clampTitleText, useThinHeader = _a.useThinHeader;
    var navigate = useLinkNavigate();
    var opts = useOpts();
    var getLocalizedString = useGetLocalizedString();
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var workspaceName = linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.workspaceName;
    var linkSolution = useLinkSolution().linkSolution;
    var isFlow = checkHackyWorkspace('Flow', workspaceName) ||
        checkHackyWorkspace('Flow Theme Testing', workspaceName);
    var isRobinhood = checkHackyWorkspace('Robinhood', workspaceName);
    var featureFlags = opts.featureFlags, fireProperEventType = opts.fireProperEventType, exit = opts.exit, history = opts.history;
    var iconColor = isRobinhood
        ? {
            type: 'theme',
            colorLegacy: 'Neutral/100',
            colorNext: 'neutral/alt',
        }
        : isFlow
            ? {
                type: 'theme',
                colorLegacy: 'Primary/50',
                colorNext: 'brand/default',
            }
            : {
                type: 'theme',
                colorLegacy: 'Primary/70',
                colorNext: 'brand/accent',
            };
    var setShowingExitConfirmation = useShowingExitConfirmation().setShowingExitConfirmation;
    var isPreviousScreenAlwaysBackable = useMemo(function () {
        if (history.length <= 1)
            return false;
        return history[1].pathname === '/account-selection';
    }, [history]);
    var showBackButton = useMemo(function () {
        if (isPreviousScreenAlwaysBackable)
            return true;
        if (hideBackButton)
            return false;
        if (onBack)
            return true;
        return !!history && history.length > 1;
    }, [hideBackButton, history, onBack, isPreviousScreenAlwaysBackable]);
    var handleBack = useCallback(function () {
        fireProperEventType(EventAction.MODAL_BACK, opts);
        if (onBack) {
            onBack();
        }
        else {
            navigate(-1);
        }
    }, [fireProperEventType, navigate, onBack, opts]);
    var handleExitButton = useCallback(function () {
        fireProperEventType(EventAction.MODAL_EXIT, opts);
        if (disableExitConfirmation || featureFlags.skipExitConfirmation) {
            exit();
        }
        else {
            setShowingExitConfirmation(true);
        }
    }, [
        disableExitConfirmation,
        exit,
        featureFlags.skipExitConfirmation,
        fireProperEventType,
        opts,
        setShowingExitConfirmation,
    ]);
    useGoBackEventListener({
        isBackable: showBackButton,
        handleBack: handleBack,
        handleExitConfirmation: handleExitButton,
        handleExit: exit,
    });
    var shouldUseThinHeader = useMemo(function () { return useThinHeader || (linkSolution && linkSolution === 'Switch Kit'); }, [useThinHeader, linkSolution]);
    var headerHeight = useMemo(function () {
        if (shouldUseThinHeader) {
            return '60px';
        }
        return '96px';
    }, [shouldUseThinHeader]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { "data-testid": testid || 'ModalHeader', style: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                height: headerHeight,
                minHeight: headerHeight,
                zIndex: 100,
            }, role: "navigation" },
            React.createElement("div", { "data-testid": "ModalSection-left", style: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px 0 24px 24px',
                    minWidth: '48px',
                    height: '48px',
                } }, showBackButton ? (React.createElement(HeaderButton, { "aria-label": "Back", "data-testid": "icon-previous", onClick: handleBack, thin: shouldUseThinHeader },
                React.createElement(Icon, { icon: "chevronLeft", size: shouldUseThinHeader ? 18 : 24, color: iconColor }))) : null),
            React.createElement("div", { "data-testid": "ModalSection-center", style: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexGrow: 1,
                    justifyContent: 'center',
                    // Fallback (IE11 / Safari <15.4): prevent text width from exceeding
                    // the container size if overflow-wrap: anywhere is unsupported
                    minWidth: 0,
                } }, title ? (React.createElement(ScreenTitleHeader, __assign({ variant: "body", weight: "bold", align: "center", color: {
                    type: 'theme',
                    colorLegacy: 'Primary/70',
                    colorNext: 'brand/accent',
                }, "data-testid": titleTestId }, (clampTitleText && {
                forceLineWrap: true,
                maxLines: 2,
            }), { as: "h2" }), title)) : showPlatformLogo ? (React.createElement(LogoHeader, null)) : null),
            React.createElement("div", { "data-testid": "ModalSection-right", style: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px 24px 24px 0',
                    minWidth: '48px',
                    height: '48px',
                } },
                onInfo && !shouldUseThinHeader ? (React.createElement(HeaderButton, { "aria-label": "Info", "data-testid": "icon-info", onClick: onInfo, margin: "0 8px 0 0", thin: shouldUseThinHeader },
                    React.createElement(Icon, { icon: "question", size: 24, color: iconColor }))) : null,
                !hideExitButton ? (React.createElement(HeaderButton, { "aria-label": "Close", "data-testid": "icon-close", onClick: handleExitButton, thin: shouldUseThinHeader }, shouldUseThinHeader ? (React.createElement(Typography, { align: "center", variant: "body", weight: "bold", color: {
                        type: 'theme',
                        colorLegacy: 'Primary/70',
                        colorNext: 'brand/accent',
                    } }, getLocalizedString('EXIT'))) : (React.createElement(Icon, { icon: "x", size: 24, color: iconColor })))) : null))));
};
export default ModalHeader;
